import { LinkIcon } from "@signet/ui";
import { api, defineWebModule, lazyPage } from "@signet/web-kit";
import { DomainsNowCard } from "./now-cards.tsx";
import { ClicksNow, VisitorsNow } from "./now-stats.tsx";
import { TopCreatorsPanel } from "./now-top.tsx";

/* ──────────────────────────────────────────────────────────────────────────
   The web half of Links: the numero-links console's screens, lifted, inside
   Signet's shell. Each route is guarded by the grant its screen reads with;
   the API checks every request regardless.
   ────────────────────────────────────────────────────────────────────────── */

/** Search params stay as the lifted screens read them: loose strings. */
const loose = (raw: Record<string, unknown>) => raw;

// The console loads when a links page is first opened, as one chunk with its
// lifted kit (web-kit lazy.ts).
const page = (name: keyof typeof import("./pages.tsx")) => lazyPage(() => import("./pages.tsx"), name);

export default defineWebModule({
  id: "links",
  nav: { label: "Links", icon: LinkIcon, to: "/links", needs: "links.link:view" },
  routes: [
    { path: "/links", component: page("LinksPage"), needs: "links.link:view", validateSearch: loose },
    { path: "/links/overview", component: page("OverviewPage"), needs: "links.analytics:view", validateSearch: loose },
    { path: "/links/creators", component: page("CreatorsPage"), needs: "links.creator:view", validateSearch: loose },
    { path: "/links/creators/$id", component: page("CreatorDetailPage"), needs: "links.creator:view" },
    { path: "/links/domains", component: page("DomainsPage"), needs: "links.domain:view", validateSearch: loose },
    { path: "/links/templates", component: page("TemplatesPage"), needs: "links.template:view" },
    { path: "/links/categories", component: page("CategoriesPage"), needs: "links.category:view" },
    { path: "/links/media", component: page("MediaPage"), needs: "links.media:view" },
    { path: "/links/analytics", component: page("AnalyticsPage"), needs: "links.analytics:view", validateSearch: loose },
    { path: "/links/activity", component: page("ActivityPage"), needs: "links.activity:view" },
    { path: "/links/settings", component: page("SettingsPage"), needs: "links.settings:view" },
  ],
  // "<name> links" is the Links tab filtered to her (Jay, 2026-10-01), which the
  // tab does for ?creator=<id>. Links has creators of its own, by id, so the
  // quick bar finds hers by name when the row is chosen.
  creatorPages: [
    {
      id: "links.creator",
      label: "Links",
      needs: "links.link:view",
      order: 20,
      keywords: "link page bio domains",
      to: async (name) => {
        const read = await api<{ creators: { id: string; name: string; deletedAt?: string | null }[] }>("/api/m/links/creators").catch(() => null);
        const found = read?.creators.find((c) => !c.deletedAt && c.name.trim().toUpperCase() === name.trim().toUpperCase());
        return found ? `/links?creator=${encodeURIComponent(found.id)}` : "/links";
      },
    },
  ],
  nowStats: [
    { id: "links.visitors", needs: "links.analytics:view", order: 10, component: VisitorsNow },
    { id: "links.clicks", needs: "links.analytics:view", order: 20, component: ClicksNow },
  ],
  // Now's order (Jay, 2026-10-28): Top creators across the top, then Revenue / Creators of the day.
  // Top links folded into Top creators the same day (Ben): each creator's row names her top link.
  nowPanels: [
    { id: "links.top-creators", needs: "links.analytics:view", order: 10, wide: true, component: TopCreatorsPanel },
  ],
  nowCards: { "links.domains": DomainsNowCard },
  commands: [
    { id: "links.list", label: "Links", to: "/links", needs: "links.link:view", hint: "Every public link page", keywords: "bio page url address slug" },
    { id: "links.overview", label: "Links overview", to: "/links/overview", needs: "links.analytics:view", hint: "Today at a glance", keywords: "dashboard health" },
    { id: "links.creators", label: "Link creators", to: "/links/creators", needs: "links.creator:view", hint: "Links", keywords: "models people" },
    { id: "links.domains", label: "Domains", to: "/links/domains", needs: "links.domain:view", hint: "Links", keywords: "hostname dns cloudflare provision health" },
    { id: "links.analytics", label: "Link analytics", to: "/links/analytics", needs: "links.analytics:view", hint: "Visits, clicks, redirects", keywords: "funnel stats traffic" },
    { id: "links.activity", label: "Links activity", to: "/links/activity", needs: "links.activity:view", hint: "Who changed what, and undo", keywords: "audit history rollback undo" },
    { id: "links.templates", label: "Link templates", to: "/links/templates", needs: "links.template:view", hint: "Links", keywords: "copy preset" },
    { id: "links.categories", label: "Link categories", to: "/links/categories", needs: "links.category:view", hint: "Links", keywords: "tags colours" },
    { id: "links.media", label: "Link images", to: "/links/media", needs: "links.media:view", hint: "Links", keywords: "photos avatars uploads" },
    { id: "links.settings", label: "Links settings", to: "/links/settings", needs: "links.settings:view", hint: "Defaults, cache, Cloudflare", keywords: "policy turnstile age gate kv d1 publish" },
  ],
});
